import { useState, useEffect, useCallback, useRef } from 'react';
import {
  DownloadSimple, UploadSimple, FileXls, FileCsv, Globe,
  CircleNotch, Check, Warning, X, ArrowRight, Database,
  Users, Buildings, Lightning, ClockCounterClockwise, FileArrowDown
} from '@phosphor-icons/react';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ImportExportPage() {
  const [stats, setStats] = useState(null);
  const [importHistory, setImportHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  // Export
  const [exportType, setExportType] = useState('combined');
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [exportCountry, setExportCountry] = useState('');
  const [exporting, setExporting] = useState(false);
  // Import
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const fetchStats = useCallback(() => {
    fetch(`${API}/import-export/stats`, { credentials: 'include' })
      .then(r => r.json()).then(setStats).catch(() => {});
  }, []);

  const fetchHistory = useCallback(() => {
    fetch(`${API}/import-export/history?limit=20`, { credentials: 'include' })
      .then(r => r.json()).then(d => setImportHistory(d.history || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchStats(); fetchHistory(); }, [fetchStats, fetchHistory]);

  const doExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ fmt: exportFormat });
      if (exportCountry) params.set('country', exportCountry);
      const endpoint = exportType === 'facilities' ? 'export/facilities' : exportType === 'contacts' ? 'export/contacts' : 'export/combined';
      const res = await fetch(`${API}/${endpoint}?${params}`, { credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `poolfix_${exportType}_${new Date().toISOString().slice(0, 10)}.${exportFormat}`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch { /* ignore */ }
    setExporting(false);
  };

  const downloadTemplate = async () => {
    const res = await fetch(`${API}/import-export/template?fmt=${exportFormat}`, { credentials: 'include' });
    if (res.ok) {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `poolfix_import_template.${exportFormat}`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleImport = async (file) => {
    if (!file) return;
    if (!file.name.match(/\.(csv|xlsx|xls)$/i)) {
      setImportResult({ error: true, message: 'Alleen .csv en .xlsx bestanden' });
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/import/leads`, {
        method: 'POST', credentials: 'include', body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        setImportResult({ success: true, ...data });
        fetchStats();
        fetchHistory();
      } else {
        setImportResult({ error: true, message: data.detail || 'Import mislukt' });
      }
    } catch (e) {
      setImportResult({ error: true, message: 'Upload mislukt' });
    }
    setImporting(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleImport(file);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="animate-fade-in" data-testid="import-export-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-black tracking-tight">Import / Export</h1>
        <p className="text-sm text-zinc-500 mt-1">Bulk import van leads en export van verrijkte data naar CSV of Excel</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-zinc-200 border border-zinc-200 mb-6" data-testid="ie-stats">
          {[
            { l: 'Faciliteiten', v: stats.exportable_facilities, c: 'text-blue-600', i: Buildings },
            { l: 'Contacten', v: stats.exportable_contacts, c: 'text-emerald-600', i: Users },
            { l: 'Imports', v: stats.total_imports, c: 'text-purple-600', i: UploadSimple },
            { l: 'Geimporteerd', v: stats.imported_facilities, c: 'text-cyan-600', i: Database },
            { l: 'Contacten Import', v: stats.imported_contacts, c: 'text-amber-600', i: Users },
          ].map((s, i) => (
            <div key={i} className="bg-white p-3 text-center">
              <s.i size={16} className="mx-auto mb-1 text-zinc-400" />
              <div className={`font-data text-xl font-bold ${s.c}`}>{s.v}</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.l}</div>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="export">
        <TabsList className="w-full justify-start border-b border-zinc-200 rounded-none bg-transparent p-0 h-auto mb-6">
          {['export', 'import', 'history'].map(tab => (
            <TabsTrigger key={tab} value={tab} data-testid={`tab-${tab}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0F172A] data-[state=active]:font-semibold data-[state=active]:shadow-none px-5 py-3 text-sm">
              {tab === 'export' ? 'Exporteren' : tab === 'import' ? 'Importeren' : 'Historiek'}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* EXPORT TAB */}
        <TabsContent value="export" className="mt-0">
          <div className="border border-zinc-200 bg-white p-6">
            <div className="flex items-center gap-2 mb-4">
              <DownloadSimple size={20} weight="bold" className="text-[#0055FF]" />
              <h3 className="font-heading text-lg font-semibold">Data Exporteren</h3>
            </div>
            <p className="text-sm text-zinc-500 mb-5">Exporteer faciliteiten, contacten of gecombineerde leads naar CSV of Excel.</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
              <div>
                <label className="text-xs font-medium text-zinc-500 mb-1 block">Type export</label>
                <Select value={exportType} onValueChange={setExportType}>
                  <SelectTrigger className="rounded-sm text-sm" data-testid="export-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="combined">Compleet (Faciliteiten + Contacten)</SelectItem>
                    <SelectItem value="facilities">Alleen Faciliteiten</SelectItem>
                    <SelectItem value="contacts">Alleen Contacten</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-500 mb-1 block">Formaat</label>
                <Select value={exportFormat} onValueChange={setExportFormat}>
                  <SelectTrigger className="rounded-sm text-sm" data-testid="export-format"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
                    <SelectItem value="csv">CSV (.csv)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-500 mb-1 block">Land (optioneel)</label>
                <Select value={exportCountry || 'all'} onValueChange={v => setExportCountry(v === 'all' ? '' : v)}>
                  <SelectTrigger className="rounded-sm text-sm"><SelectValue placeholder="Alle landen" /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    <SelectItem value="all">Alle landen</SelectItem>
                    <SelectItem value="NL">Nederland</SelectItem>
                    <SelectItem value="BE">Belgie</SelectItem>
                    <SelectItem value="DE">Duitsland</SelectItem>
                    <SelectItem value="FR">Frankrijk</SelectItem>
                    <SelectItem value="GB">Verenigd Koninkrijk</SelectItem>
                    <SelectItem value="ES">Spanje</SelectItem>
                    <SelectItem value="IT">Italie</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <button data-testid="export-btn" onClick={doExport} disabled={exporting}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-[#0055FF] text-white rounded-sm hover:bg-blue-700 disabled:opacity-50 transition-colors">
              {exporting ? <CircleNotch size={14} className="animate-spin" /> : <DownloadSimple size={16} weight="bold" />}
              {exporting ? 'Exporteren...' : `Exporteer ${exportType === 'combined' ? 'Leads' : exportType === 'facilities' ? 'Faciliteiten' : 'Contacten'}`}
            </button>

            {/* Quick export cards */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { type: 'combined', label: 'Complete Leads', desc: 'Faciliteiten + contacten in 1 bestand', icon: Database, count: stats ? `${stats.exportable_facilities} faciliteiten, ${stats.exportable_contacts} contacten` : '' },
                { type: 'facilities', label: 'Faciliteiten', desc: 'Alle zwembaden en sportcentra', icon: Buildings, count: stats ? `${stats.exportable_facilities} records` : '' },
                { type: 'contacts', label: 'Contacten', desc: 'Alle contactpersonen met verrijkte data', icon: Users, count: stats ? `${stats.exportable_contacts} records` : '' },
              ].map(q => (
                <button key={q.type} onClick={() => { setExportType(q.type); doExport(); }}
                  className="border border-zinc-200 rounded-sm p-4 text-left hover:bg-zinc-50 hover:border-[#0055FF] transition-all group" data-testid={`quick-export-${q.type}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <q.icon size={16} className="text-zinc-400 group-hover:text-[#0055FF]" />
                    <span className="text-sm font-medium">{q.label}</span>
                    <DownloadSimple size={12} className="ml-auto text-zinc-300 group-hover:text-[#0055FF]" />
                  </div>
                  <p className="text-xs text-zinc-500">{q.desc}</p>
                  <p className="text-[10px] text-zinc-400 mt-1 font-data">{q.count}</p>
                </button>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* IMPORT TAB */}
        <TabsContent value="import" className="mt-0">
          <div className="border border-zinc-200 bg-white p-6">
            <div className="flex items-center gap-2 mb-4">
              <UploadSimple size={20} weight="bold" className="text-[#0055FF]" />
              <h3 className="font-heading text-lg font-semibold">Leads Importeren</h3>
            </div>
            <p className="text-sm text-zinc-500 mb-2">Upload een CSV of Excel bestand met faciliteiten en contacten.</p>
            <p className="text-xs text-zinc-400 mb-5">Kolommen worden automatisch herkend. Ondersteund: Naam, Stad, Land, Email, Telefoon, Website, Type, Contactpersoon, Contact Email, Functie.</p>

            {/* Download template */}
            <button onClick={downloadTemplate} className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-[#0055FF] border border-[#0055FF]/30 rounded-sm hover:bg-blue-50 mb-5 transition-colors" data-testid="download-template-btn">
              <FileArrowDown size={14} /> Download Import Template (.{exportFormat})
            </button>

            {/* Drop zone */}
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-sm p-10 text-center cursor-pointer transition-colors ${dragOver ? 'border-[#0055FF] bg-blue-50' : 'border-zinc-300 hover:border-zinc-400 hover:bg-zinc-50'}`}
              data-testid="import-dropzone"
            >
              <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
                onChange={e => handleImport(e.target.files?.[0])} data-testid="import-file-input" />
              {importing ? (
                <div className="flex items-center gap-2 justify-center text-[#0055FF]">
                  <CircleNotch size={20} className="animate-spin" />
                  <span className="text-sm font-medium">Importeren...</span>
                </div>
              ) : (
                <>
                  <UploadSimple size={32} className="mx-auto mb-2 text-zinc-300" />
                  <div className="text-sm font-medium text-zinc-600">Sleep een bestand hierheen of klik om te uploaden</div>
                  <div className="text-xs text-zinc-400 mt-1">CSV of Excel (.xlsx) — max 10MB</div>
                </>
              )}
            </div>

            {/* Import Result */}
            {importResult && (
              <div className={`mt-4 p-4 rounded-sm border ${importResult.error ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`} data-testid="import-result">
                <div className="flex items-center gap-2 mb-2">
                  {importResult.error ? <Warning size={16} className="text-red-500" /> : <Check size={16} className="text-emerald-600" />}
                  <span className="text-sm font-semibold">{importResult.error ? 'Import mislukt' : 'Import succesvol!'}</span>
                </div>
                {importResult.error ? (
                  <p className="text-sm text-red-600">{importResult.message}</p>
                ) : (
                  <div className="space-y-1 text-sm">
                    <div className="flex gap-4">
                      <span className="text-zinc-500">Rijen verwerkt:</span>
                      <span className="font-data font-bold">{importResult.total_rows}</span>
                    </div>
                    <div className="flex gap-4">
                      <span className="text-zinc-500">Faciliteiten aangemaakt:</span>
                      <span className="font-data font-bold text-blue-600">{importResult.facilities_created}</span>
                    </div>
                    <div className="flex gap-4">
                      <span className="text-zinc-500">Contacten aangemaakt:</span>
                      <span className="font-data font-bold text-emerald-600">{importResult.contacts_created}</span>
                    </div>
                    {importResult.error_count > 0 && (
                      <div className="mt-2 text-xs text-amber-600">
                        <Warning size={12} className="inline mr-1" /> {importResult.error_count} rij(en) met fouten
                        {importResult.errors?.map((e, i) => <div key={i} className="text-zinc-400 ml-4">- {e}</div>)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        {/* HISTORY TAB */}
        <TabsContent value="history" className="mt-0">
          <div className="border border-zinc-200 overflow-hidden" data-testid="import-history">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Bestand</th>
                  <th className="text-center px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Rijen</th>
                  <th className="text-center px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Faciliteiten</th>
                  <th className="text-center px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Contacten</th>
                  <th className="text-center px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Fouten</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Datum</th>
                </tr>
              </thead>
              <tbody>
                {importHistory.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-zinc-400 text-xs">Nog geen imports. Upload een CSV of Excel bestand.</td></tr>
                ) : importHistory.map((h, i) => (
                  <tr key={h.import_id || i} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        {h.filename?.endsWith('.csv') ? <FileCsv size={16} className="text-emerald-500" /> : <FileXls size={16} className="text-blue-500" />}
                        <span className="text-sm font-medium">{h.filename}</span>
                      </div>
                      <div className="text-[10px] text-zinc-400 mt-0.5">{h.import_id}</div>
                    </td>
                    <td className="px-4 py-2.5 text-center font-data">{h.total_rows}</td>
                    <td className="px-4 py-2.5 text-center font-data text-blue-600">{h.facilities_created}</td>
                    <td className="px-4 py-2.5 text-center font-data text-emerald-600">{h.contacts_created}</td>
                    <td className="px-4 py-2.5 text-center">
                      {h.errors?.length > 0 ? (
                        <Badge className="rounded-full px-2 py-0.5 text-xs bg-amber-100 text-amber-700">{h.errors.length}</Badge>
                      ) : (
                        <Check size={14} className="mx-auto text-emerald-500" />
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-zinc-500">
                      {h.imported_at ? new Date(h.imported_at).toLocaleString('nl-NL') : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
